import { useState } from "react";

import { AlertTriangle, Trash2 } from "lucide-react";

import { ConfirmationModal } from "@components/ConfirmationModal";
import {
  Banner,
  BannerAction,
  BannerIcon,
  BannerTitle,
} from "@components/ui/banner";

type RecordingNotSentBannerProps = {
  /** Whether audio was captured; a transcript-only session has nothing to download. */
  hasAudio: boolean;
  /** A retry is in flight. */
  isRetrying?: boolean;
  onRetry: () => void;
  onDownload: () => void;
  /** Called only after the user confirms. */
  onDiscard: () => void;
};

/**
 * Shown in place of "Recording complete" when the stopped session's summarize
 * request fails (MDRS-551). The saved recording is kept until the upload is
 * accepted, so there's no dismiss ×: the doctor retries, downloads a copy, or
 * deliberately discards it behind a confirmation.
 */
export const RecordingNotSentBanner = ({
  hasAudio,
  isRetrying = false,
  onRetry,
  onDownload,
  onDiscard,
}: RecordingNotSentBannerProps) => {
  const [isDiscardOpen, setIsDiscardOpen] = useState(false);

  return (
    <>
      <Banner variant="destructive" inset role="alert" className="flex-wrap">
        <BannerIcon>
          <AlertTriangle />
        </BannerIcon>
        <BannerTitle className="min-w-48 space-y-0.5">
          <span className="block text-sm font-semibold text-foreground">
            Recording not sent for transcription
          </span>
          <span className="block text-xs font-normal text-foreground-muted">
            {hasAudio
              ? "Your audio is kept in this browser for now. Retry, or download a copy to be safe."
              : "Retry to send the live transcript for summarising."}
          </span>
        </BannerTitle>
        <div className="flex shrink-0 flex-wrap gap-2">
          <BannerAction variant="default" isLoading={isRetrying} onClick={onRetry}>
            Retry
          </BannerAction>
          {hasAudio && (
            <BannerAction disabled={isRetrying} onClick={onDownload}>
              Download audio
            </BannerAction>
          )}
          <BannerAction
            variant="destructive-ghost"
            disabled={isRetrying}
            onClick={() => setIsDiscardOpen(true)}
          >
            Discard
          </BannerAction>
        </div>
      </Banner>

      <ConfirmationModal
        type="warning"
        icon={<Trash2 className="size-10 text-destructive" />}
        title="Discard recording?"
        description={
          hasAudio
            ? "The audio for this session will be permanently deleted from this device. Download a copy first if you might need it. This can't be undone."
            : "The live transcript for this session will be permanently deleted. This can't be undone."
        }
        confirmButtonText="Discard recording"
        cancelButtonText="Keep recording"
        open={isDiscardOpen}
        onOpenChange={setIsDiscardOpen}
        onConfirm={() => {
          setIsDiscardOpen(false);
          onDiscard();
        }}
        onCancel={() => setIsDiscardOpen(false)}
      />
    </>
  );
};
